/**
 * <sizeme-bridge> — three jobs for the SizeMe size-recommendation widget:
 *
 * 1. Loads the SizeMe library (cdn.sizeme.com/store/sizeme.js) lazily, when the size-guide drawer
 *    that holds its `.sizeme-container` opens. SizeMe skips a container that's inside a display:none
 *    drawer on page load and never retries, so eager-loading leaves it blank on the storefront (the
 *    theme editor only works because it re-renders the section with the drawer open). Loading once
 *    the container has layout mirrors that visible-at-init condition — and avoids fetching SizeMe
 *    for shoppers who never open the size guide.
 *
 * 2. Checks SizeMe's product endpoint before loading the widget. Products that SizeMe does not
 *    support return no usable measurements, so their "Find your size" section stays hidden.
 *
 * 3. Keeps the hidden Dawn-format Size mirror (snippets/sizeme-size-mirror.liquid) in sync with the
 *    real variant picker, so SizeMe (which parses the mirror via its `crasman-koo` selector) always
 *    sees the shopper's current size. The visible picker uses name="options[Size]" + <ul><li>, which
 *    SizeMe can't read; the mirror is the plain <input name="Size"> markup its parser expects. On
 *    every `variant:changed` we copy the selected size onto the mirror and fire a native `change`.
 *    Read-only bridge: it never writes back to the real picker.
 */

const SIZEME_SRC = 'https://cdn.sizeme.com/store/sizeme.js';
const SIZEME_CONTEXT = 'https://www.sizeme.com';

class SizemeBridge extends HTMLElement {
  #availabilityController = null;
  #availabilityPromise = null;
  #io = null;
  #loaded = false;
  #phObserver = null;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.optionName = this.dataset.sizeOption;
    this.mirror = this.querySelector('fieldset');
    this.sectionId = this.closest('product-component')?.dataset.sectionId;
    // The real picker lives in the same .pdp-variants block; fall back to the first on the page.
    this.host =
      this.closest('.pdp-variants')?.querySelector('variant-selects') ||
      document.querySelector('variant-selects');

    if (this.host && this.mirror && this.optionName) {
      this.host.addEventListener('variant:changed', this.#onVariantChange);
      // Align once on load in case the picker's default differs from the mirror's rendered state.
      this.#sync();
    }

    this.#setupLibraryLoad();
    this.#setupPlaceholders();
  }

  disconnectedCallback() {
    this.host?.removeEventListener('variant:changed', this.#onVariantChange);
    document.removeEventListener('click', this.#onOpenClick, true);
    this.#availabilityController?.abort();
    this.#io?.disconnect();
    this.#phObserver?.disconnect();
  }

  // ---- SizeMe library loading -------------------------------------------------

  #setupLibraryLoad() {
    // Theme editor re-renders the section with the drawer open, so load right away there.
    if (window.Shopify?.designMode) {
      void this.#loadLibrary();
      return;
    }
    // Primary: load when the size-guide drawer (which holds .sizeme-container) is opened.
    document.addEventListener('click', this.#onOpenClick, true);
    // Fallback: also load if a container gains viewport visibility another way (deep link, etc.).
    const containers = this.#findSizeSections().flatMap((section) => [
      ...section.querySelectorAll('.sizeme-container'),
    ]);
    if (containers.length) {
      this.#io = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void this.#loadLibrary();
      });
      containers.forEach((container) => this.#io.observe(container));
    }
  }

  #onOpenClick = (event) => {
    const trigger = event.target.closest?.('[data-action="open-drawer"]');
    if (!trigger || trigger.dataset.toggleId !== `size-guide-${this.sectionId}`) return;
    void this.#loadLibrary();
  };

  async #loadLibrary() {
    if (this.#loaded) return;

    // Hide synchronously during the drawer's opening click so unsupported products never flash an
    // empty heading/panel. The section is restored before SizeMe initializes when data is available.
    this.#setFindSizeVisible(false);

    const isAvailable = await this.#isProductAvailable();
    if (!this.isConnected || !isAvailable || this.#loaded) return;

    this.#setFindSizeVisible(true);
    this.#loaded = true;
    document.removeEventListener('click', this.#onOpenClick, true);
    this.#io?.disconnect();
    this.#io = null;

    const script = document.createElement('script');
    script.src = SIZEME_SRC;
    script.defer = true;
    script.dataset.sizemeLib = '';
    script.addEventListener('error', () => this.#setFindSizeVisible(false), { once: true });
    document.body.appendChild(script);
  }

  #isProductAvailable() {
    if (this.#availabilityPromise) return this.#availabilityPromise;

    const product = window.sizeme_product;
    const productId = product?.SKU;
    const variantIds = new Set(Object.keys(product?.item ?? {}));
    if (!productId || variantIds.size === 0) return Promise.resolve(false);

    const contextAddress = (window.sizeme_options?.contextAddress || SIZEME_CONTEXT).replace(/\/$/, '');
    this.#availabilityController = new AbortController();
    this.#availabilityPromise = fetch(
      `${contextAddress}/api/products/${encodeURIComponent(productId)}`,
      {
        headers: { Accept: 'application/json' },
        signal: this.#availabilityController.signal,
      }
    )
      .then(async (response) => {
        if (!response.ok || response.status === 204) return false;

        const productData = await response.json();
        if (String(productData.itemType ?? '').startsWith('0')) return false;

        return Object.keys(productData.measurements ?? {}).some((variantId) =>
          variantIds.has(variantId)
        );
      })
      .catch(() => {
        // A failed availability request means the SizeMe widget cannot render for this visit.
        this.#availabilityPromise = null;
        return false;
      });

    return this.#availabilityPromise;
  }

  #findSizeSections() {
    if (!this.sectionId) return [];

    return [...document.querySelectorAll(`[data-sizeme-section="${CSS.escape(this.sectionId)}"]`)];
  }

  #setFindSizeVisible(visible) {
    this.#findSizeSections().forEach((section) => {
      section.toggleAttribute('hidden', !visible);
    });
  }

  // ---- Field-name placeholders ------------------------------------------------
  // SizeMe renders each measurement name as a <label> above the field. The Almada design wants that
  // name INSIDE the field as its placeholder (the label is kept but visually hidden via CSS). SizeMe
  // renders/re-renders asynchronously and swaps the measurements per product (Chest/Arm vs pants
  // Waist/Hips/Outseam), so an observer re-applies the placeholder on every re-render.

  #setupPlaceholders() {
    const containers = document.querySelectorAll('.sizeme-container');
    if (!containers.length) return;
    // Watch node changes only (not attributes) — setting placeholder/aria-label won't retrigger.
    this.#phObserver = new MutationObserver(() => this.#applyPlaceholders());
    containers.forEach((container) => this.#phObserver.observe(container, { childList: true, subtree: true }));
    this.#applyPlaceholders();
  }

  #applyPlaceholders() {
    document.querySelectorAll('.sizeme-container .measurement-input-table .measurement-cell').forEach((cell) => {
      const label = cell.querySelector('.measurement-label');
      const input = cell.querySelector('input');
      const name = label?.textContent.trim();
      if (input && name) {
        if (input.placeholder !== name) input.placeholder = name;
        if (input.getAttribute('aria-label') !== name) input.setAttribute('aria-label', name);
      }
      // Clickable cm unit → give it a proper touch target (WCAG 2.5.8, theme .hot-spot-mini).
      const units = cell.querySelector('.units');
      if (units && !units.classList.contains('hot-spot-mini')) units.classList.add('hot-spot-mini');
    });
  }

  // ---- Size mirror sync -------------------------------------------------------

  #onVariantChange = () => this.#sync();

  // Current size = the checked radio in the real picker's Size fieldset (matched by option name).
  #currentSize() {
    const fieldset = this.host.querySelector(
      `.pdp-variant-option[data-option-name="${CSS.escape(this.optionName)}"]`
    );
    return fieldset?.querySelector('.pdp-variant-option__input:checked')?.value ?? null;
  }

  #sync() {
    const size = this.#currentSize();
    if (size == null) return;

    let changed = false;
    this.mirror.querySelectorAll('input[type="radio"]').forEach((radio) => {
      const shouldCheck = radio.value === size;
      if (radio.checked !== shouldCheck) {
        radio.checked = shouldCheck;
        changed = true;
      }
    });

    if (!changed) return;
    // Fire native change so SizeMe's parser re-reads the selected size.
    const checked = this.mirror.querySelector('input[type="radio"]:checked');
    checked?.dispatchEvent(new Event('change', { bubbles: true }));
    this.mirror.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('sizeme-bridge')) {
  customElements.define('sizeme-bridge', SizemeBridge);
}
